import { Injectable } from "@angular/core";
import { PRIMITIVE, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { OrderDataLookupService } from "./OrderDataLookupService";
import { ModelUpdate } from "../../impl/ModelUpdate";
import {
  PrimaryBlotterLookupService,
  ExtendedOrderEntryLogType,
} from "../../impl/PrimaryBlotterLookupService";

import { IOrderDataPromiseLookupService } from "../IOrderDataPromiseLookupService";

import { OrderLifecycleRateSubscriptionSource } from "../../../rate/impl/OrderLifecycleRateSubscriptionSource";
import { OrderDataModelUpdateSource } from "./OrderDataPromiseLookupService";
import { Subject } from "rxjs";

import { RawDataOrderAdapter } from "../../../mediator/adapter/RawDataOrderAdapter";
import { SysFeatures } from "../../../model/core/SysFeatures";
import { OrderDataPrivatizationService } from "../../../model/privatization/OrderDataPrivatizationService";
import { IOrderEntryEvent } from "../../../model/core/IOrderEntryEvent";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { UserLookupService } from "./UserLookupService";
import { PauseOrderData } from "shared/cubiq-model-multi-asset";
import { OrderDataImpl } from "shared/cubiq-model-multi-asset";
import { TraderGroupLookupService } from "./TraderGroupLookupService";
import { CurrencyPairCalendarPromiseLookupService } from "./CurrencyPairCalendarPromiseLookupService";
import { RateCache } from "../../../rate/impl/RateCache";
import { UserTemperatureService } from "../../../profile/impl/UserTemperatureService";
import { Temperature } from "../../../model/Temperature";
import { SystemConfigPromiseService } from "shared/auth-services";
import { DetailedOrderStatusRequestPrimaryService } from "../../../action/impl/DetailedOrderStatusRequestPrimaryService";
import { StaticDataPrivatizationType } from "../../../model/privatization/enum/StaticDataPrivatizationType";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ActiveUser } from "../../../model/core/ActiveUser";
import { ProfilePromiseActionService } from "../../../profile/impl/ProfilePromiseActionService";

import { CcyPairLookupService } from "./CcyPairLookupService";

import { NotificationService } from "../../notification/impl/NotificationService";
export const BLOTTERS_ORDERS: string[] = ["Working Orders"];
export const PRIMING_BATCH_SIZE: number = 100;

export const EVENT_TYPE_INFLIGHT_CHECK_STATE: string = "InflightCheckStateEvent";

class PendingOrderUpdate {
  // eslint-disable-next-line @typescript-eslint/ban-types
  constructor(public event: any, public processEventFunction: Function) {}
}

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OrdersTableDefinitionFactory } from "../../../model/OrdersTableDefinitionFactory";

import { ClientLookupService } from "./ClientLookupService";
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXAppNotificationType } from "../../../model/core/notifications/BFXAppNotificationType";
import { BFXNotificationSubType } from "../../../model/core/notifications/BFXNotificationSubType";
import { BFXNotificationEventType } from "../../../model/core/notifications//BFXNotificationEventType";
import { BFXAppNotificationEventType } from "../../../model/core/notifications//BFXAppNotificationEventType";
import { filter, retry } from "rxjs/operators";
import { checkMonitoredOrderState } from "src/shared/auth-services/active-user/model/VisibilityModel";
import { EXTENDED_INFO_IS_ORDER_ENTRY } from "shared/cubiq-services/model/notifications/NotificationEventProcessorService";
import {
  TRANSFERED_OUT_OF_VISIBILITY,
  TransferUtils,
} from "shared/cubiq-services/model/orders/TransferUtils";
import { IOrderMigrationEvent } from "shared/cubiq-services/model/core/IOrderMigrationEvent";

@Injectable()
export class OrderDataPrimaryLookupService
  extends PrimaryBlotterLookupService<string, CubiqTS.OrderDataImpl>
  implements IOrderDataPromiseLookupService
{
  protected rateOrderMap: Map<string, Set<string>> = new Map();
  protected pairOrderMap: Map<string, Set<string>> = new Map();
  protected calendarOrderMap: Map<string, Set<string>> = new Map();
  protected orderUpdates: Array<any> = [];
  protected orderEntryReferences: Set<string> = new Set();
  protected orderTempCoolOffSecs: number = 900; // 15mins default, will need to read a sys config
  protected orderTempMap: Map<string, { temp: Temperature; lastHot: number }> = new Map();
  protected providerDownMap: Map<string, { provider: string; feedDown: boolean }> = new Map();
  protected rateStaleMap: Map<string, { instrument: string; stale: boolean }> = new Map();
  protected transferUpdateSubject: Subject<any> = new Subject<any>();
  private activeUser: ActiveUser;
  protected queueEvents: boolean = true;

  /*
    depending on flow Lock Lifecycle may be
    received out of order with Order Lifecycle events
    mainly rollbacks. 

    FXBFXOM-9765 refers to unlock bug only
    so defer keeping eventTypeId until needed
   */
  private lockJournal: Map<
    string,
    {
      lockType: CubiqTS.LockType;
      lockUserId: CubiqTS.Long;
      // eventTypeId: CubiqTS.FxLifeCycleEventType,
      orderRevisionId: string;
    }
  > = new Map();

  /** Emits events to notify listeners that orders are in migration */
  private modelOrderMigrationSubject: Subject<any> = new Subject<IOrderMigrationEvent>();

  constructor(
    protected base: OrderDataLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    protected userService: UserLookupService,
    protected traderGroupService: TraderGroupLookupService,
    dataAdapter: RawDataOrderAdapter,
    userTemps: UserTemperatureService,
    private clientLookup: ClientLookupService,
    private sysConfigService: SystemConfigPromiseService,
    private privatizationService: OrderDataPrivatizationService,
    private detailedOrderStatusService: DetailedOrderStatusRequestPrimaryService,
    private activeUserService: ActiveUserProfilePromiseService,
    private currencyPairCalendarService: CurrencyPairCalendarPromiseLookupService,
    private profileService: ProfilePromiseActionService,
    private ccyPairLookupService: CcyPairLookupService,
    ordersTableDefinitionFactory: OrdersTableDefinitionFactory,
    notifService: NotificationService,
    private rateCache: RateCache,
  ) {
    super(
      base,
      factory,
      CubiqTS.OrderDataImpl,
      cubiqWorker,
      "OrderDataImpl",
      CubiqTS.OrderLifeCycleEventImpl,
      dataAdapter,
      notifService,
    );

    // this.orderDataAggreationService.setDataAdapter(dataAdapter);
    this.broker.registerMethod(
      "getOrderData",
      [PRIMITIVE],
      this.getOrderData.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getOrderReference",
      [PRIMITIVE],
      this.getOrderReference.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getOrderDataForIds",
      [PRIMITIVE],
      this.getOrderDataForIds.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "hasOrdersForRateKey",
      [PRIMITIVE],
      this.hasOrdersForRateKey.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "addOrderEntryReference",
      [PRIMITIVE],
      this.addOrderEntryReference.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "removeOrderEntryReference",
      [PRIMITIVE],
      this.removeOrderEntryReference.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "hasOrderEntryReference",
      [PRIMITIVE],
      this.hasOrderEntryReference.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getModifyPreInitDataForOrderId",
      [PRIMITIVE],
      this.getModifyPreInitDataForOrderId.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getAllOrderDatas",
      [],
      this.getAllOrderDatas.bind(this),
      this.VALUE,
    );

    logger.out.info("OrderDataPrimaryLookupService: primeDataProfile waiting on isAuth()");
    this.sysConfigService.getSystemConfig(SysFeatures.hotNotifCoolDownSecs).then((values) => {
      if (values) {
        this.orderTempCoolOffSecs = Number(values);
      }
    });

    let dependencyPromise: Promise<Array<any>> = Promise.all([
      this.privatizationService.isReady(),
      this.activeUserService.getActiveUserData(),
    ]);

    {
      cubiqWorker.isAuth(`${this.constructor.name}`).then(() => {
        dependencyPromise.then((values: Array<any>) => {
          this.activeUser = values[1];
          console.debug("OrderDataPrimaryLookupService: primeDataProfile isAuth() resolved");
          let orderDataObservable = cubiqWorker
            .primeDataProfile(BLOTTERS_ORDERS, CubiqTS.OrderDataImpl.name)
            .subscribe(
              (orderData: CubiqTS.OrderDataImpl[]) => {
                // privatization processing
                if (Array.isArray(orderData) && orderData.length > 0) {
                  for (let orderDataItem of orderData) {
                    this.privatizationService.checkPrivatization(orderDataItem);
                  }
                }

                this.base.prime(orderData, false);
              },
              (e: Error) => {
                logger.out.error("OrderDataPrimaryLookupService: Failed to prime order data: " + e);
                this._primed.reject(e);
              },
              () => {
                this.base.prime([], true);
                try {
                  this.postprocessPrimedData();
                  logger.out.info(
                    "OrderDataPrimaryLookupService: Successfully postprocessPrimedData order data",
                  );
                  this.subscribeForCalendars().then(
                    (res) => {
                      this.primed = true;

                      this._primed.resolve(true);
                    },
                    (reason) => {
                      logger.out.error("OrderDataPrimaryLookupService: Failed to prime: " + reason);
                      this._primed.resolve(false);
                    },
                  );
                } catch (ex) {
                  logger.out.error(
                    "OrderDataPrimaryLookupService: Error resolving order relationships.",
                    ex,
                  );
                }
              },
            );

          this.base.flashManager.flashUpdateSubject.subscribe(this.onOrderFlash.bind(this));

          // let aggregateMessageCount = 0;
          // this.orderDataAggreationService.aggregateUpdate$.subscribe((aggregationData: OrderDataAggregationUpdate) => {
          //   if (!this.sendAggregateUpdates) {
          //     // don't send aggregate updates before first getAdaptedDataWithAggregateData() resolved
          //     // as related priming/adapting in progress
          //     return;
          //   }

          //   // let startTime = performance.now();
          //   if (aggregationData.aggregateModelObject != null) {
          //     super.fireAdaptedDataUpdateEvent(aggregationData.aggregateModelObject, {}, true);
          //   }

          //   if (aggregationData.previousAggregateModelObject != null) {
          //     super.fireAdaptedDataUpdateEvent(aggregationData.previousAggregateModelObject, {}, true);
          //   }

          //   if (Array.isArray(aggregationData.modelObjects))
          //       aggregationData.modelObjects.forEach(modelObject => {
          //           super.fireAdaptedDataUpdateEvent(modelObject, {}, true);
          //       });

          //   if (aggregationData.removeId !== null) {
          //     super.fireModelDeleteEvent(aggregationData.removeId, true);
          //   }
          // });
        });
      });
    }

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.OrderLifeCycleEventImpl.name)
      .pipe(
        filter(($event: CubiqTS.OrderLifeCycleEventImpl) => {
          logger.out.info(
            `orderdata-s - Order Id=${$event.orderData?.orderId} ` +
              `Order Type=${CubiqTS.OrderType[$event.orderData?.orderType]} ` +
              `State=${CubiqTS.OrderState[$event.orderData?.orderState]}`,
          );
          return true;
        }),
      )
      .pipe(filter(($event) => !this.benchmarksFilter($event)))
      .subscribe((event: CubiqTS.OrderLifeCycleEventImpl) => {
        if (event != null && this.queueEvents) {
          this.logOrderEntryDebugInfo(event, "QUEUED");
          this.orderUpdates.push(
            new PendingOrderUpdate(event, this.processOrderLifecycleEvent.bind(this)),
          );
        } else {
          this.logOrderEntryDebugInfo(event, "PROCESS");
          logger.out.info(
            `orderdata-s - Order Id=${
              event.orderData?.orderId
            }, txTime=${event.orderData?.txTime.format("HHmmss.SSS")}`,
          );
          if (
            event.orderData != null &&
            this.shouldProcessUpdate(event.orderData as CubiqTS.OrderDataImpl, event)
          ) {
            this.processOrderLifecycleEvent(event);
            this.processOrderLifecycleEventForOrderEntry(event);
          }
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.LockLifeCycleEventImpl.name)
      .pipe(filter(($event) => !this.benchmarksFilter($event)))
      .subscribe((event: CubiqTS.LockLifeCycleEventImpl) => {
        if (event != null && this.queueEvents) {
          this.logOrderEntryDebugInfo(event, "QUEUED");
          this.orderUpdates.push(
            new PendingOrderUpdate(event, this.processLockLifecycleEvent.bind(this)),
          );
        } else {
          this.logOrderEntryDebugInfo(event, "PROCESS");
          this.processLockLifecycleEvent(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.XferEventImpl.name)
      .pipe(filter(($event) => !this.benchmarksFilter($event)))
      .subscribe((event: CubiqTS.XferEventImpl) => {
        if (event != null && this.queueEvents) {
          this.orderUpdates.push(new PendingOrderUpdate(event, this.processXferEvent.bind(this)));
        } else {
          this.processXferEvent(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BulkOrderLifeCycleEventImpl.name)
      .pipe(filter(($event) => !this.benchmarksFilter($event)))
      .subscribe((event: CubiqTS.BulkOrderLifeCycleEventImpl) => {
        if (event != null && this.queueEvents) {
          this.logOrderEntryDebugInfo(event, "QUEUED");
          this.orderUpdates.push(
            new PendingOrderUpdate(event, this.processBulkOrderLifecycleEvent.bind(this)),
          );
        } else {
          this.logOrderEntryDebugInfo(event, "PROCESS");
          this.processBulkOrderLifecycleEvent(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.TrailingStopEventImpl.name)
      .subscribe((event: CubiqTS.TrailingStopEventImpl) => {
        let order: CubiqTS.OrderDataImpl = this.base.getItem(event.orderId.toString());
        if (order) {
          // this.orderDataAggreationService.invalidateOrderData(order);
          let trailingStopDetails: CubiqTS.TrailingStopDetailsImpl =
            order.trailingStopDetails as CubiqTS.TrailingStopDetailsImpl;
          trailingStopDetails.isTrackingActive = event.isTrailActive;
          order.requestRt = event.requestRate;

          logger.out.info(
            "TrailingStopEventImpl:" +
              order.orderId +
              ":" +
              (order.orderState ? CubiqTS.OrderState[order.orderState] : "") +
              " ReqRT: " +
              order.requestRt +
              " TxTime: " +
              order.txTime +
              " Trail Active:" +
              event.isTrailActive +
              " TSL Ev. Time: " +
              event.timeStamp,
          );
          this.fireModelUpdateEvent(order, {
            source: OrderDataModelUpdateSource.TrailingStopEvent,
            evtMsgId: event.messageId,
            // override cached value
            markedUpRequestRate: null,
          });
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.PauseLifeCycleEventImpl.name)
      .subscribe((event: CubiqTS.PauseLifeCycleEventImpl) => {
        if (event != null && this.queueEvents) {
          this.logOrderEntryDebugInfo(event, "QUEUED");
          this.orderUpdates.push(
            new PendingOrderUpdate(event, this.processPauseLifecycleEvent.bind(this)),
          );
        } else {
          this.logOrderEntryDebugInfo(event, "PROCESS");
          this.processPauseLifecycleEvent(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.UnsolicitedCancelLifeCycleEventImpl.name)
      .pipe(
        filter(($event) =>
          this.filterOutHomors($event as CubiqTS.UnsolicitedCancelLifeCycleEventImpl),
        ),
      )
      .subscribe((event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl) => {
        if (event != null && this.queueEvents) {
          this.logOrderEntryDebugInfo(event, "QUEUED");
          this.orderUpdates.push(
            new PendingOrderUpdate(event, this.processUnsolCanLifecycleEvent.bind(this)),
          );
        } else {
          this.logOrderEntryDebugInfo(event, "PROCESS");
          this.processUnsolCanLifecycleEvent(event);
        }
      });

    currencyPairCalendarService.getTdrNotification().subscribe((pairs: Set<string>) => {
      pairs.forEach((pair) => {
        if (this.base.hasActivePair(pair)) {
          let orderIds: Set<string> = this.base.getActiveOrderIdsByAssetCode(pair);

          for (const orderId of orderIds) {
            this.fireModelUpdateByOrderId(orderId);
          }
        }
      });
    });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.AdjustedTenorForHolidayEventImpl.name)
      .subscribe((event: CubiqTS.AdjustedTenorForHolidayEventImpl) => {
        this.processAdjustedTenorForHoliday(event);
      });

    // Observe application messages
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.ApplicationMessageEventImpl.name)
      .subscribe(this.processInflightApplicationMessage.bind(this));
  }

  // Returns TRUE if event is for Benchmark, FALSE otherwise
  private benchmarksFilter($event: any): boolean {
    let result: boolean = false;
    if ($event instanceof CubiqTS.ConcreteEncodable) {
      switch ($event.encodableType) {
        case "com.bcdfx.model.event.lifecycle.OrderLifeCycleEventImpl":
          $event = $event as CubiqTS.OrderLifeCycleEventImpl;
          result =
            $event.orderData instanceof CubiqTS.OrderDataImpl &&
            $event.orderData.orderType === CubiqTS.OrderType.BENCHMARK;
          break;
        case "com.bcdfx.model.event.lifecycle.BenchmarkLockLifeCycleEventImpl":
          result = true; // BenchmarkLockLifeCycleEventImpl => lock event type only for benchmarks
          break;
        case "com.bcdfx.model.event.lifecycle.XferEventImpl":
          $event = $event as CubiqTS.XferEventImpl;
          result =
            Array.isArray($event.orderData) &&
            $event.orderData[0] instanceof CubiqTS.OrderDataImpl &&
            $event.orderData[0].orderType === CubiqTS.OrderType.BENCHMARK;
          break;
        case "com.bcdfx.model.InterventionEventImpl":
          $event = $event as CubiqTS.InterventionEventImpl;
          result =
            $event.orderData instanceof CubiqTS.OrderDataImpl &&
            $event.orderData.orderType === CubiqTS.OrderType.BENCHMARK;
          break;
        case "com.bcdfx.model.event.lifecycle.BulkOrderLifeCycleEventImpl":
          let bulkEvent: CubiqTS.BulkOrderLifeCycleEventImpl =
            $event as CubiqTS.BulkOrderLifeCycleEventImpl;

          // If it's previsibility bulk event it is a benchmark bulk event
          result = bulkEvent.eventTypeId === CubiqTS.FxLifeCycleEventType.BULK_PREVISIBILITY;

          if (
            !result &&
            Array.isArray(bulkEvent.events) &&
            bulkEvent.events.length > 0 &&
            bulkEvent.events[0] instanceof CubiqTS.OrderLifeCycleEventImpl
          ) {
            return this.benchmarksFilter(bulkEvent.events[0]);
          }

          break;
      }
    }
    //logger.out.info(`BMARK: ORDER-DATA-LOOKUP Event Filter result: ${result ? 'BENCHMARK': 'NOT BENCHMARK'} for ${$event.encodableType} => $event:`, $event);
    return result;
  }

  protected shouldProcessUpdate(
    order: CubiqTS.OrderDataImpl,
    lifecycleEvent: CubiqTS.OrderLifeCycleEventImpl = null,
  ): boolean {
    try {
      if (this.isALoopReactivating(order, lifecycleEvent)) {
        this.stopIgnoring(order.orderId);
      } else if (
        order?.orderType != CubiqTS.OrderType.ALGO &&
        order?.orderType != CubiqTS.OrderType.BMALGO &&
        this.shouldIgnoreAsTerminal(order.orderId)
      ) {
        logger.out.warn(
          `Skipping order data update for order ${order.orderId} as marked internally as Terminal`,
        );
        return false;
      }

      // Currently the same as orderId.toStr but can change
      const orderKey = this.base.getItemKey(order);
      let oldItem: CubiqTS.OrderDataImpl = this.base.getItem(orderKey);

      if (oldItem != null && order != null && order.txTime != null && oldItem.txTime != null) {
        let ret: boolean = order.txTime.isAfter(oldItem.txTime);

        // If it's a rollback event and the transaction time is the same of the new and existing data than we process it
        if (
          !ret &&
          lifecycleEvent != null &&
          lifecycleEvent.rollbackEventFg === true &&
          order.txTime.isSame(oldItem.txTime)
        ) {
          ret = true;
        }

        if (!ret && order.orderId != null) {
          logger.out.warn(
            `${this.constructor.name}: Skipping order data update for order ${orderKey} message ID ${order.messageId}.`,
          );
        }

        return ret;
      }
    } catch (error) {
      logger.out.warn(
        "Error determining if an order update should be processed. Update will be processed.",
        order,
        error,
      );
    }

    return order != null;
  }

  // partial implementation, doing triggered first so others can be added as stories are hit.
  convertEventTypeToNotifType(eventType: CubiqTS.FxLifeCycleEventType): BFXNotificationType {
    switch (eventType) {
      case CubiqTS.FxLifeCycleEventType.TI_REQUIRED: //should the other acceptance be here also?
      case CubiqTS.FxLifeCycleEventType.PENDING_CANCELLATION:
        return BFXNotificationType.ACCEPTANCE_NEEDED;
      case CubiqTS.FxLifeCycleEventType.GFT_ACTIVATE:
        return BFXNotificationType.ACTIVATED;
      case CubiqTS.FxLifeCycleEventType.CANCELLED:
        return BFXNotificationType.CANCELLED;
      case CubiqTS.FxLifeCycleEventType.EXPIRED:
        return BFXNotificationType.EXPIRED;
      case CubiqTS.FxLifeCycleEventType.TRIGGERED:
        return BFXNotificationType.TRIGGERED;
      case CubiqTS.FxLifeCycleEventType.SUSPENDED:
        return BFXNotificationType.SUSPENDED;
      case CubiqTS.FxLifeCycleEventType.EXPIRING:
        return BFXNotificationType.EXPIRING;
      case CubiqTS.FxLifeCycleEventType.SUSPENDING:
        return BFXNotificationType.SUSPENDING;
      case CubiqTS.FxLifeCycleEventType.ACTIVATING:
        return BFXNotificationType.ACTIVATING;
    }
    return undefined;
  }

  convertEventTypeToAppNotifType(eventType: CubiqTS.FxLifeCycleEventType): BFXAppNotificationType {
    switch (eventType) {
      case CubiqTS.FxLifeCycleEventType.GFT_ACTIVATE:
        return BFXAppNotificationType.ACTIVATED;
      case CubiqTS.FxLifeCycleEventType.CANCELLED:
        return BFXAppNotificationType.CANCELLED;
      case CubiqTS.FxLifeCycleEventType.EXPIRED:
        return BFXAppNotificationType.EXPIRED;
      case CubiqTS.FxLifeCycleEventType.SUSPENDED:
        return BFXAppNotificationType.SUSPENDED;
      case CubiqTS.FxLifeCycleEventType.FILLED:
        return BFXAppNotificationType.FILLED;
    }
    return undefined;
  }

  convertOrderFlagsToSubType(orderData: CubiqTS.OrderDataImpl): BFXNotificationSubType {
    switch (orderData.orderState) {
      case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
        if (orderData.lockType === CubiqTS.LockType.MODIFY_LOCK) {
          return BFXNotificationSubType.ACCEPTANCE_MODIFY_IN;
        }
        return BFXNotificationSubType.ACCEPTANCE_NEW;
      case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_CANCEL;
      case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_MODIFY_OUT;
      case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_MIGRATION;
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_EXPIRY;
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_TRIGGER_EXPIRY;
    }
    // temp transfer check
    if (orderData.targetDeskId && orderData.targetDeskId.greaterThan(-1)) {
      return BFXNotificationSubType.ACCEPTANCE_TRANSFER;
    }
    return undefined;
  }

  protected processOrderLifecycleEvent(event: CubiqTS.OrderLifeCycleEventImpl): any {
    let ordUpdat = null;
    let deleteId: string = null;

    // try {
    let orderData: CubiqTS.OrderDataImpl = event.orderData as CubiqTS.OrderDataImpl;
    if (this.isInTerminalState(orderData)) {
      this.ignoreAsTerminal(orderData.orderId);
    }

    // If client segment is empty and we have an existing order populate it from there
    try {
      if (orderData.clientSegment == null && orderData?.orderState == CubiqTS.OrderState.DELETED) {
        let orderId = orderData?.orderId?.toString ? orderData?.orderId?.toString() : null;
        if (orderId != null) {
          let existing = this.base.getOrderData(orderData.orderId.toString());
          if (existing != null) {
            orderData.clientSegment = existing.clientSegment;
            orderData.clientIsTraderVisible = existing.clientIsTraderVisible;
          }
        }
      }
    } catch (err) {
      logger.out.warn(
        `${
          this.constructor.name
        }: Failed to extract client segment for deleted order ${orderData?.orderId?.toString()}`,
        err,
      );
    }

    try {
      this.privatizationService.checkPrivatization(orderData);
    } catch (err) {
      logger.out.error(
        `${
          this.constructor.name
        }: Failed durring checking privatization of order: ${orderData?.orderId?.toString()}`,
        err,
      );
    }

    if (orderData.lockType == CubiqTS.LockType.CANCEL_LOCK) {
      orderData.lockType = null;
    }

    orderData = this.processLockJournal(event);

    if (this.detailFillIds && this.detailFillIds.has(orderData.orderId.toString())) {
      // If user still has order locked, then don't remove it - e.g. vol/min agent can cause update event when locked for fill
      if (!orderData.lockUserId || orderData.lockUserId.toString() != this.activeUser.userId) {
        this.detailFillIds.delete(orderData.orderId.toString());
        this.fireModelDetailFillClearEvent(orderData.orderId.toString(), null, orderData.orderRef);
      }
    }

    const { notifType, notifSubType, appNotifType, queueId } = this.determineNotifType(event);

    let shouldNotifyFg: boolean = this.notifService.shouldNotify(notifType, orderData.orderType);
    let shouldAppNotifyFg: boolean = this.notifService.shouldAppNotify(
      appNotifType,
      orderData.orderType,
    );
    let existingOrder = this.base.getOrderData(orderData.orderId.toString());

    if (
      existingOrder &&
      event.eventTypeId === CubiqTS.FxLifeCycleEventType.FILLED_PARTIAL &&
      !this.isAlgoForwardOrderTerminating(event.orderData, existingOrder)
    ) {
      // In some cases, PART_FILL on server can pick up unsupported states when fill arrives during other flows
      // Ensure part-fill order state is same as what we have from current LC events, before visibility check
      // this will ensure order is not marked for deletion accidentally
      const oldState = existingOrder.orderState;
      orderData.orderState = oldState;
      // Appears that existing lockType is not being copied - this is needed for algo DI_NEEDED visibility
      orderData.lockType = existingOrder.lockType;
    }
    if (existingOrder && event.eventTypeId === CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE) {
      // Appears that existing lockType is not being copied - this is needed for algo DI_NEEDED visibility
      orderData.lockType = existingOrder.lockType;
    }

    const canSeeOrderData = this.activeUser.canSeeOrder(orderData);
    if (orderData != null && (!canSeeOrderData || !this.checkOrderPropagation(orderData))) {
      if (existingOrder) {
        if (shouldNotifyFg) {
          this.notifService.fireNotificationEvent(
            this.notifService.orderDataToNotification(orderData, notifType, notifSubType),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
            queueId,
          );
        } else {
          this.notifService.fireNotificationClearEvent(
            this.notifService.dataToClearEvent([orderData.orderId.toString()]),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
          );
          // this.fireModelNotificationClearEvent([orderData.orderId.toString()]);
        }
        if (shouldAppNotifyFg) {
          this.notifService.fireAppNotificationEvent(
            this.notifService.orderDataToNotification(orderData, notifType),
            BFXAppNotificationEventType.ORDER_UPDATE_EVENT,
            queueId,
          );
          // this.fireAppModelNotificationEvent(existingOrder,{appNotifType:appNotifType});
        }
      }
      if (event.fxOrderLifeCycleEvent !== CubiqTS.FxLifeCycleEventType.MODIFICATION_INFO) {
        // When we modify info for a PENDING order we do not want to fire a Model DElete event as this
        // is indisinguishable from an order going out of scope, which disables monitoring
        this.orderTempMap.delete(orderData.orderId.toString());
        this.fireModelDeleteEvent(orderData.orderId.toString());
        deleteId = orderData.orderId.toString();
      }

      /*  if(event.eventTypeId === CubiqTS.FxLifeCycleEventType.REJECTED && this.orderEntryReferences.has(event.externalClientReference)){
                        this.fireModelErrorEvent(event.externalClientReference, event.errors);
                    }
                    // Order Entry events
                    this.forwardOrderEntryEvent(event, orderData, null); */
    } else {
      let order: CubiqTS.OrderDataImpl;
      if (event.fxOrderLifeCycleEvent === CubiqTS.FxLifeCycleEventType.MODIFICATION_INFO) {
        order = this.base.getOrderData(orderData.orderId.toString());
        if (order) {
          order.comments1 = orderData.comments1;
          order.comments2 = orderData.comments2;
          order.clientTraderName = orderData.clientTraderName;
        }
      } else if (event.fxOrderLifeCycleEvent === CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE) {
        order = this.base.getOrderData(orderData.orderId.toString());
        if (order) {
          // this.orderDataAggreationService.invalidateOrderData(order);
          order.creditRevalDetails = orderData.creditRevalDetails;
          this.base.updateItem(order);
        }
      } else if (
        event.fxOrderLifeCycleEvent === CubiqTS.FxLifeCycleEventType.FILLED_PARTIAL &&
        !this.isTerminalAlgo(orderData)
      ) {
        order = this.base.getOrderData(orderData.orderId.toString());

        if (order) {
          order.filledBaseAmt = orderData.filledBaseAmt;
          order.executedQty = orderData.executedQty;
          order.orderQty = orderData.orderQty;
          order.leavesQty = orderData.leavesQty;
          order.settleQty = orderData.settleQty;
          order.usdEquivQty = orderData.usdEquivQty;
          order.leavesSettleQty = orderData.leavesSettleQty;
          order.usdEquivLeavesQty = orderData.usdEquivLeavesQty;
          order.executedSettleQty = orderData.executedSettleQty;
          order.usdEquivExecutedQty = orderData.usdEquivExecutedQty;
          order.lastFillDetails = orderData.lastFillDetails;
          order.vwapAllInRt = orderData.vwapAllInRt;
          order.vwapSpotRt = orderData.vwapSpotRt;
          order.marketVwapAllInRt = orderData.marketVwapAllInRt;
          order.marketVwapSpotRt = orderData.marketVwapSpotRt;
          order.fillState = orderData.fillState;
          event.orderData = order;
          this.base.updateItem(order);
        }
      } else {
        order = orderData;
        this.base.mapItem(order);
        if (!shouldNotifyFg && this.shouldClear(event)) {
          this.notifService.fireNotificationClearEvent(
            this.notifService.dataToClearEvent([order.orderId.toString()]),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
          );
        }
      }

      if (order) {
        let extraProps = {
          source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
        };
        if (shouldNotifyFg) {
          this.notifService.fireNotificationEvent(
            this.notifService.orderDataToNotification(orderData, notifType, notifSubType),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
            queueId,
          );
        }

        // NB TODO What to do here?
        //  if(order.orderType !== CubiqTS.OrderType.BENCHMARK) {
        //     this.orderDataAggreationService.invalidateOrderData(order);
        ordUpdat = this.fireModelUpdateEvent(
          order,
          Object.assign(extraProps, {
            evtTypeId: event.eventTypeId,
            evtMsgId: event.messageId,
          }),
        );
        //	}

        if (shouldAppNotifyFg) {
          // this.fireAppModelNotificationEvent(order,{appNotifType:appNotifType});
          this.notifService.fireAppNotificationEvent(
            this.notifService.orderDataToNotification(orderData, notifType, notifSubType),
            BFXAppNotificationEventType.ORDER_UPDATE_EVENT,
            queueId,
          );
        }
        // if(shouldAppNotifyFg){
        //     this.fireAppModelNotificationEvent(order,{appNotifType:appNotifType});
        // }
        //this.forwardOrderEntryEvent(event, orderData, ordUpdat );
      }
    }
    // } catch (error) {
    //     logger.out.warn('Failed to propagate model update/delete event.', error);
    // }
    return { update: ordUpdat, delete: deleteId };
  }

  private addLockJournalEntry(
    event: CubiqTS.LockLifeCycleEventImpl,
    order: CubiqTS.OrderDataImpl,
    orderId: string,
  ) {
    if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.UNLOCKED) {
      let orderRevisionId = order?.orderRevisionId?.toString();
      this.lockJournal.set(orderId, {
        lockType: event.lockType,
        lockUserId: event.lockUserId,
        // eventTypeId: event.eventTypeId,
        orderRevisionId: orderRevisionId,
      });
      logger.out.info(
        `${
          this.constructor.name
        }.addLockJournalEntry() ${orderId}#${orderRevisionId} restored lock from lifecycle event CubiqTS.LockType.${
          CubiqTS.LockType[event.lockType]
        }`,
      );
    } else {
      this.lockJournal.delete(orderId);
    }
  }

  private processLockJournal(event: CubiqTS.OrderLifeCycleEventImpl) {
    const orderData: CubiqTS.OrderDataImpl = event.orderData;
    const orderId = orderData?.orderId?.toString();
    const orderRevisionId = orderData?.orderRevisionId?.toString();

    if (
      event?.rollbackEventFg &&
      CubiqTS.FxLifeCycleEventType.PENDING == event?.fxOrderLifeCycleEvent &&
      this.lockJournal.has(orderData.orderId.toString())
    ) {
      const v = this.lockJournal.get(orderId);
      orderData.lockType = v.lockType;
      orderData.lockUserId = v.lockUserId;
      if (v.lockType == null) {
        logger.out.info(
          `${this.constructor.name}.processLockJournal() ${orderId}#${orderRevisionId}:${v.orderRevisionId} restored unlock from lifecycle event`,
        );
      } else {
        logger.out.info(
          `${this.constructor.name}.processLockJournal() ${orderId}#${orderRevisionId}:${
            v.orderRevisionId
          }  restored lock from lifecycle event CubiqTS.LockType.${
            CubiqTS.LockType[orderData.lockType]
          }`,
        );
      }
      this.lockJournal.delete(orderId);
    } else if (
      this.lockJournal.has(orderData.orderId.toString()) &&
      Array.isArray(event?.errors?.errorMessages) &&
      event?.errors.errorMessages.length === 0 &&
      (event?.fxOrderLifeCycleEvent == CubiqTS.FxLifeCycleEventType.LOCKED ||
        event?.fxOrderLifeCycleEvent == CubiqTS.FxLifeCycleEventType.UNLOCKED)
    ) {
      this.lockJournal.delete(orderId);
      logger.out.info(
        `${this.constructor.name}.processLockJournal() ${orderId} clear entry valid order lifecycle`,
      );
    }

    return orderData;
  }

  /**
   * EMit events specific to the OrderEntry subjects - this method does not change any internal state
   * @param event
   */
  public processOrderLifecycleEventForOrderEntry(event: CubiqTS.OrderLifeCycleEventImpl): void {
    if (event.eventTypeId != CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE) {
      let orderEntryIsListening = this.orderEntryReferences.has(event.externalClientReference);
      let orderData: CubiqTS.OrderDataImpl =
        event.orderData != null && event.orderData instanceof CubiqTS.OrderDataImpl
          ? event.orderData
          : null;

      if (orderData != null) {
        // && orderData.orderType !== CubiqTS.OrderType.BENCHMARK){
        logger.out.info(`${this.constructor.name} orderId:${orderData.orderId.toString()} seen`);
        const client = this.clientLookup.getClient(orderData.clientCode);
        const privatiseOrder = client && StaticDataPrivatizationType.hasPrivatization(client);

        const metaData: IOrderEntryEvent = {
          orderId: event.orderId,
          orderRef: orderData.orderRef,
          ref: orderData.clOrdID,
          state: orderData.orderState,
          evtType: event.eventTypeId,
          messageId: event.messageId,
          lockEvent: false,
          errors: event.errors,
          orderErrors: orderData.orderErrors,
          requestedOrderType: orderData.requestedOrderType,
          privatiseOrder: privatiseOrder,
          txTime: orderData.txTime,
          executedQty: orderData.executedQty,
          vwapAllInRt: orderData.vwapAllInRt,
          rollbackEventFg: event.rollbackEventFg,

          tgcId: null,
          sgcId: null,
          deskCode: null,
          user: null,
        };
        if (orderEntryIsListening) {
          // define lock user
          let lockUser: any = null;
          if (
            CubiqTS.FxLifeCycleEventType.SI_REQUIRED === event.eventTypeId &&
            orderData.siLockUserId instanceof CubiqTS.Long
          ) {
            lockUser = orderData.siLockUserId.toString();
          } else if (orderData.lockUserId instanceof CubiqTS.Long) {
            lockUser = orderData.lockUserId.toString();
          }
          // define trader and sales groups
          const traderGroupCodeId = orderData.deskId?.toString() ?? null;
          const salesGroupCodeId = orderData.salesGroupId?.toString() ?? null;
          let deskCode = null;
          if (traderGroupCodeId !== null) {
            deskCode = this.traderGroupService.getItem(traderGroupCodeId).groupCode;
          }
          // transform data
          if (orderData.lockType === CubiqTS.LockType.CANCEL_LOCK) {
            orderData.lockType = null;
          }
          Object.assign(metaData, {
            tgcId: traderGroupCodeId,
            sgcId: salesGroupCodeId,
            deskCode: deskCode,
            user: lockUser,
          });
          //
          //logger.out.info(`${this.constructor.name}.processOrderLifecycleEventForOrderEntry %O`, metaData);
          this.fireModelOrderEntryEvent(metaData);
        } else if (
          orderData.orderState === CubiqTS.OrderState.IN_MODIFICATION ||
          orderData.orderState === CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED ||
          orderData.orderState === CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED
        ) {
          if (orderData.lockUserId) {
            metaData.user = orderData.lockUserId.toString();
          }
          if (!event.rollbackEventFg) {
            this.fireOrderEntryClearEvent(metaData);
          }
        } else {
          this.logOrderEntryDebugInfo(
            event,
            "Not subscribed",
            ExtendedOrderEntryLogType.FORWARD_IGNORE_SIMPLE,
          );
        }
      } else if (
        orderEntryIsListening &&
        (event.eventTypeId === CubiqTS.FxLifeCycleEventType.REJECTED ||
          event.eventTypeId === CubiqTS.FxLifeCycleEventType.BULK_REJECTED)
      ) {
        if (!event.rollbackEventFg) {
          this.fireModelErrorEvent(event, event.externalClientReference);
        }
      } else {
        this.logOrderEntryDebugInfo(
          event,
          "No orderData",
          ExtendedOrderEntryLogType.FORWARD_IGNORE_SIMPLE,
        );
      }
    } else {
      this.logOrderEntryDebugInfo(
        event,
        "",
        ExtendedOrderEntryLogType.FORWARD_IGNORE_EXTENDED,
        event.orderData,
      );
    }
  }

  private determineNotifType(event: CubiqTS.OrderLifeCycleEventImpl): {
    notifType: BFXNotificationType;
    notifSubType: BFXNotificationSubType;
    appNotifType: BFXAppNotificationType;
    queueId: string | null;
  } {
    let notifType: BFXNotificationType = this.convertEventTypeToNotifType(event.eventTypeId);
    let appNotifType: BFXAppNotificationType = this.convertEventTypeToAppNotifType(
      event.eventTypeId,
    );
    let notifSubType: BFXNotificationSubType;
    let queueId: string = null;

    if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.ACTIVE) {
      let existingOrder = this.base.getOrderData(event.orderData.orderId.toString());
      if (existingOrder) {
        if (existingOrder.lockType === CubiqTS.LockType.MODIFY_LOCK) {
          if (
            existingOrder.lockUserId &&
            existingOrder.lockUserId.toString() === this.cubiqWorker.getUserId()
          ) {
            notifType = undefined;
            appNotifType = undefined;
          } else {
            notifType = BFXNotificationType.MODIFIED;
            appNotifType = BFXAppNotificationType.MODIFIED;
          }
        } else if (
          CubiqTS.OrderState.PENDING == existingOrder.orderState ||
          CubiqTS.OrderState.SUSPENDED == existingOrder.orderState
        ) {
          notifType = BFXNotificationType.ACTIVATED;
          appNotifType = BFXAppNotificationType.ACTIVATED;
        }
      }
    }

    if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.FILLED) {
      let orderId = event.orderData.orderId.toString();
      let existingOrder = this.base.getOrderData(orderId);
      if (existingOrder) {
        if (
          existingOrder.orderState != CubiqTS.OrderState.FILLED &&
          event.orderData.orderState === CubiqTS.OrderState.FILLED
        ) {
          notifType = BFXNotificationType.FILLED;
          notifSubType = BFXNotificationSubType.FILL_FULL;
          appNotifType = BFXAppNotificationType.FILLED;
          queueId = orderId;
        }
      }
    }

    if (notifType === BFXNotificationType.ACCEPTANCE_NEEDED) {
      notifSubType = this.convertOrderFlagsToSubType(event.orderData);
      if (notifSubType === undefined) {
        // unknown type of acceptance so we dont want to show a corrupted notification
        notifType = undefined;
      } else if (
        notifSubType === BFXNotificationSubType.ACCEPTANCE_TRANSFER &&
        event.orderData.transferUserId &&
        event.orderData.transferUserId.toString() === this.cubiqWorker.getUserId()
      ) {
        notifType = undefined;
        notifSubType = undefined;
      } else if (
        notifSubType === BFXNotificationSubType.ACCEPTANCE_NEW &&
        event.orderData.entryUserId &&
        event.orderData.entryUserId.toString() === this.cubiqWorker.getUserId()
      ) {
        notifType = undefined;
        notifSubType = undefined;
      } else if (
        (notifSubType === BFXNotificationSubType.ACCEPTANCE_MODIFY_OUT ||
          notifSubType === BFXNotificationSubType.ACCEPTANCE_CANCEL ||
          notifSubType === BFXNotificationSubType.ACCEPTANCE_MODIFY_IN) &&
        event.orderData.lockUserId &&
        event.orderData.lockUserId.toString() === this.cubiqWorker.getUserId()
      ) {
        notifType = undefined;
        notifSubType = undefined;
      }
    } else if (notifType === BFXNotificationType.CANCELLED) {
      if (
        event.orderData.cancelUserId &&
        event.orderData.cancelUserId.toString() === this.cubiqWorker.getUserId()
      ) {
        notifType = undefined;
        notifSubType = undefined;
        appNotifType = undefined;
      }
    } else if (notifType === BFXNotificationType.TRIGGERED) {
      let existingOrder = this.base.getOrderData(event.orderData.orderId.toString());
      if (existingOrder && existingOrder.orderState === CubiqTS.OrderState.TRIGGERED) {
        if (
          existingOrder.leavesQty &&
          existingOrder.leavesQty.isGreaterThan(event.orderData.leavesQty)
        ) {
          // this was a part fill dont show the notification
          notifType = undefined;
        }
        if (existingOrder.isAboutToSuspend != event.orderData.isAboutToSuspend) {
          notifType = undefined;
        }
      }
    }

    let entryNotification: boolean = event.orderData?.extendedInfo?.has(
      EXTENDED_INFO_IS_ORDER_ENTRY,
    );
    if (
      entryNotification &&
      event.orderData?.entryUserId?.toString() != this.cubiqWorker.getUserId()
    ) {
      notifType = BFXNotificationType.LIMITALGO_ORDER_ENTERED;
      notifSubType = BFXNotificationSubType.ENTRY;
      appNotifType = BFXAppNotificationType.ENTERED;
    }

    return {
      notifType: notifType,
      notifSubType: notifSubType,
      appNotifType: appNotifType,
      queueId: queueId,
    };
  }

  //
  protected processPauseLifecycleEvent(event: CubiqTS.PauseLifeCycleEventImpl): any {
    if (event.orders != null) {
      event.orders.forEach((incoming: PauseOrderData) => {
        let order: CubiqTS.OrderDataImpl = this.base.getItem(incoming.orderId.toString());

        if (order != null) {
          let previous: CubiqTS.PauseState = order?.pauseState;
          let incomingPauseState: CubiqTS.PauseState = incoming?.pauseState;
          let errorsUpdated: boolean = false;
          let isUnsolicitedEvent: boolean = event.unsolicited;

          // NOTIFICATIONS - Flow Control variables
          let determinedNotifTypes = this.determinePauseResumeNotificationType(
            event.eventTypeId,
            incomingPauseState,
            isUnsolicitedEvent,
          );
          let notifType: BFXNotificationType = determinedNotifTypes.notifType;
          let notifSubType: BFXNotificationSubType = determinedNotifTypes.notifSubType;

          let shouldNotifyFg: boolean = this.notifService.shouldNotify(notifType, order.orderType);

          if (shouldNotifyFg) {
            this.notifService.fireNotificationEvent(
              this.notifService.orderDataToNotification(order, notifType, notifSubType),
              BFXNotificationEventType.ORDER_UPDATE_EVENT,
            );
          } else {
            this.notifService.fireNotificationClearEvent(
              this.notifService.dataToClearEvent([order.orderId.toString()]),
              BFXNotificationEventType.ORDER_UPDATE_EVENT,
            );
          }

          if (
            incoming.errors != null &&
            incoming.isError &&
            Array.isArray(incoming.errors.errorMessages) &&
            incoming.errors.errorMessages.length > 0
          ) {
            order.orderErrors = incoming.errors;
            errorsUpdated = true;
          } else if (order.orderErrors) {
            order.orderErrors = null;
            errorsUpdated = true;
          }

          // Don't infer an incoming.pauseState
          // absence means errors carry significance
          // (e.g. Reject) or server bug -dc
          if (incoming.pauseState != null) order.pauseState = incoming.pauseState;

          if (order.pauseState != previous || errorsUpdated) {
            this.fireModelUpdateEvent(order, {
              source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
              evtTypeId: event.eventTypeId,
              evtMsgId: event.messageId,
              errors: incoming.isError ? incoming.errors : null,
            });
          }
        }
      });
    }
  }

  private determineUnsolCanNotificationType(event: CubiqTS.FxLifeCycleEventType): {
    notifType: BFXNotificationType;
  } {
    let notifType = undefined;

    if (event == CubiqTS.FxLifeCycleEventType.UNSOLICITED_CANCEL) {
      notifType = BFXNotificationType.UNSOLICITED_CANCEL;
    }

    return {
      notifType: notifType,
    };
  }
  //

  protected processUnsolCanLifecycleEvent(event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl): any {
    if (event.benchmarkFg == false && event.orders != null) {
      event.orders.forEach((incoming: CubiqTS.OrderData) => {
        if (event._extendedInfo.get("makerId") != null) {
          let makerId = event._extendedInfo.get("makerId");
          let makerIdNum: number = +makerId;
          incoming.deskId = new CubiqTS.Long(makerIdNum);
        }

        if (this.activeUser.canSeeOrder(incoming)) {
          if (incoming != null) {
            // NOTIFICATIONS - Flow Control variables
            let notifType: BFXNotificationType = BFXNotificationType.UNSOLICITED_CANCEL;
            let shouldNotifyFg: boolean = this.notifService.shouldNotify(
              BFXNotificationType.UNSOLICITED_CANCEL,
              incoming.orderType,
            );
            let maker: string = event._extendedInfo.get("maker");
            let opsId: string = event._extendedInfo.get("opsId");
            if (shouldNotifyFg) {
              this.notifService.fireNotificationEvent(
                this.notifService.orderDataToUnsolCanNotification(
                  incoming,
                  notifType,
                  event.alternateGroupId?.toString() ?? "",
                  event.alternateGroupName,
                  maker,
                  opsId,
                ),
                BFXNotificationEventType.ORDER_UPDATE_EVENT,
              );
            } else {
              this.notifService.fireNotificationClearEvent(
                this.notifService.dataToClearEvent([incoming.orderId.toString()]),
                BFXNotificationEventType.ORDER_UPDATE_EVENT,
              );
            }
          }
        }
      });
    }
  }

  /*
  Filter out hedge order monitor orders.
  */
  private filterOutHomors($event: any): boolean {
    let result: boolean = true;

    if ($event instanceof CubiqTS.ConcreteEncodable) {
      if (
        $event.encodableType ==
        "com.bcdfx.model.event.lifecycle.UnsolicitedCancelLifeCycleEventImpl"
      ) {
        let event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl =
          $event as CubiqTS.UnsolicitedCancelLifeCycleEventImpl;
        event.orders.forEach((orderData: CubiqTS.OrderData) => {
          result = !(
            orderData instanceof CubiqTS.OrderDataImpl &&
            orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
            orderData.fixingDetails.bmOrderType === CubiqTS.BenchmarkOrderType.PositionOrder
          );
        });
      }
    }

    return result;
  }

  private determinePauseResumeNotificationType(
    event: CubiqTS.FxLifeCycleEventType,
    incomingPauseState: CubiqTS.PauseState,
    isUnsolicitedEvent: boolean,
  ): { notifType: BFXNotificationType; notifSubType: BFXNotificationSubType } {
    let notifType = undefined;
    let notifSubType = undefined;

    if (event == CubiqTS.FxLifeCycleEventType.PAUSED && isUnsolicitedEvent) {
      if (incomingPauseState != null) {
        notifType = BFXNotificationType.UNSOLICITED_PAUSE_RESUME;
        switch (incomingPauseState) {
          case CubiqTS.PauseState.PAUSED:
            notifSubType = BFXNotificationSubType.UNSOLICITED_PAUSE;
            break;
          case CubiqTS.PauseState.NOT_PAUSED:
            notifSubType = BFXNotificationSubType.UNSOLICITED_RESUME;
            break;
          default:
            notifSubType = undefined;
        }
      }

      return {
        notifType: notifType,
        notifSubType: notifSubType,
      };
    }
    return { notifType: notifType, notifSubType: notifSubType };
  }

  protected processLockLifecycleEvent(event: CubiqTS.LockLifeCycleEventImpl): any {
    if (event.lockType != CubiqTS.LockType.CANCEL_LOCK) {
      if (event.orderIds != null && Array.isArray(event.orderIds)) {
        event.orderIds.forEach((orderId) => {
          if (orderId != null) {
            let order: CubiqTS.OrderDataImpl = this.base.getItem(orderId.toString());
            if (order != null) {
              order.lockType = event.lockType;
              order.lockUserId = event.lockUserId;

              this.fireModelUpdateEvent(order, {
                source: OrderDataModelUpdateSource.LockLifeCycleEvent,
                evtTypeId: event.eventTypeId,
                evtMsgId: event.messageId,
              });

              this.addLockJournalEntry(event, order, orderId);

              if (this.orderEntryReferences.has(order.clOrdID)) {
                const client = this.clientLookup.getClient(order.clientCode);
                let privatiseOrder = client && StaticDataPrivatizationType.hasPrivatization(client);
                let deskCode =
                  order.deskId != null
                    ? this.traderGroupService.getItem(order.deskId.toString()).groupCode
                    : null;
                let metaData: IOrderEntryEvent = {
                  orderId: orderId,
                  orderRef: order.orderRef,
                  ref: order.clOrdID,
                  state: order.orderState,
                  evtType: event.eventTypeId,
                  tgcId: null,
                  sgcId: null,
                  user: event.lockUserId ? event.lockUserId.toString() : null,
                  messageId: event.messageId,
                  lockEvent: true,
                  deskCode: deskCode,
                  errors: null,
                  orderErrors: order.orderErrors,
                  requestedOrderType: order.requestedOrderType,
                  privatiseOrder: privatiseOrder,
                  executedQty: order.executedQty,
                  vwapAllInRt: order.vwapAllInRt,
                  rollbackEventFg: false,
                };
                this.fireModelOrderEntryEvent(metaData, order);
              }
            }

            if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.LOCKED) {
              for (let orderData of event.orderDatas) {
                // don't check this.detailFillIds for pts
                this.rateCache.setDetailFillPtsLock(
                  this.ccyPairLookupService.getCcyPair(orderData.baseCode, orderData.nonBaseCode),
                  orderData,
                );
              }
            } else if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.UNLOCKED) {
              if (this.detailFillIds && this.detailFillIds.has(orderId)) {
                let orderRef = order != null ? order.orderRef : this.detailFillIds.get(orderId);
                this.detailFillIds.delete(orderId);
                let dfItem = this.fireModelDetailFillClearEvent(
                  orderId,
                  event.lockUser,
                  orderRef,
                  false,
                );
                this.modelDetailFillEventSubject.next(dfItem);
              }
              // don't check this.detailFillIds for pts
              this.rateCache.clearDetailFillPtsLock(orderId);
            }
          }
        });
      }
    }
  }

  protected processXferEvent(event: CubiqTS.XferEventImpl): void {
    let clearNotifs: Array<string> = [];

    let hasOrderData: boolean =
      event.orderData != null && Array.isArray(event.orderData) && event.orderData.length > 0;

    const xferData = TransferUtils.getXferEventAdditionalData(
      event,
      this.userService,
      this.traderGroupService,
    );

    // The event either has order data and order IDs matching the order data or just order IDs
    // If no order data is found process the order IDs
    if (!hasOrderData && Array.isArray(event.orderIds)) {
      let orderIDs: Array<CubiqTS.Long> = event.orderIds;
      logger.out.info("XferEventImpl.orderID.count[" + orderIDs.length + "]");

      // Publish to a separate subject as existing listeners are reliant on order data
      // If we publish existing order data from base we risk overwriting events that are in
      // progress if processed out of sequence

      event.orderIds.forEach((orderId: CubiqTS.Long) => {
        // Check to make sure we're not processing a transfer for an order that we've already cancelled - FXBFXOM-5914
        if (this.shouldIgnoreAsTerminal(orderId)) {
          logger.out.warn(
            `Skipping order xfer update for order ${orderId} as marked internally as Terminal`,
          );
        } else {
          let transferUpdateEvent: any = Object.assign({}, xferData);
          transferUpdateEvent.orderId = orderId;
          transferUpdateEvent.messageId = event.messageId;
          this.transferUpdateSubject.next(transferUpdateEvent);
        }
      });

      logger.out.info("XferEventImpl.orderID.Processed");
    } else {
      logger.out.info("XferEventImpl.count[" + event.orderData.length + "]");
      for (let order of event.orderData) {
        if (this.shouldProcessUpdate(order as CubiqTS.OrderDataImpl)) {
          this.privatizationService.checkPrivatization(order);
          // should do a selective readapt here only a small subset of data can be updated based on a transfer
          this.base.mapItem(order as CubiqTS.OrderDataImpl);

          let orderData: OrderDataImpl = order as OrderDataImpl;
          if (
            orderData &&
            orderData.siblingRelationship &&
            orderData.siblingRelationship.orderId &&
            !orderData.siblingRelationship.order
          ) {
            let sibOrder = this.base.getOrderData(orderData.siblingRelationship.orderId);
            orderData.siblingRelationship.order = sibOrder;
          }
          if (
            orderData != null &&
            (!this.activeUser.canSeeOrder(orderData) || !this.checkOrderPropagation(orderData))
          ) {
            this.fireModelDeleteEvent(orderData.orderId.toString());
            this.fireModelDeleteWithReasonEvent(
              orderData.orderId.toString(),
              TRANSFERED_OUT_OF_VISIBILITY,
              order,
              xferData,
            );
          } else {
            if (
              event.eventType == CubiqTS.XferType.XFER_REQUEST &&
              this.notifService.shouldNotify(
                BFXNotificationType.ACCEPTANCE_NEEDED,
                orderData.orderType,
              )
            ) {
              if (
                orderData.transferUserId &&
                orderData.transferUserId.toString() !== this.cubiqWorker.getUserId()
              ) {
                if (this.activeUser.canSeeOrderInInbox(orderData)) {
                  this.notifService.fireNotificationEvent(
                    this.notifService.orderDataToNotification(
                      orderData,
                      BFXNotificationType.ACCEPTANCE_NEEDED,
                      BFXNotificationSubType.ACCEPTANCE_TRANSFER,
                    ),
                    BFXNotificationEventType.ORDER_UPDATE_EVENT,
                  );
                } else {
                  clearNotifs.push(order.orderId.toString());
                }
              }
            } else if (
              event.eventType == CubiqTS.XferType.XFER_AUTO_MIGRATION &&
              orderData.orderState === CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED
            ) {
              if (
                this.notifService.shouldNotify(
                  BFXNotificationType.ACCEPTANCE_NEEDED,
                  orderData.orderType,
                )
              ) {
                if (this.activeUser.canSeeOrderInInbox(orderData)) {
                  this.notifService.fireNotificationEvent(
                    this.notifService.orderDataToNotification(
                      orderData,
                      BFXNotificationType.ACCEPTANCE_NEEDED,
                      BFXNotificationSubType.ACCEPTANCE_MIGRATION,
                    ),
                    BFXNotificationEventType.ORDER_UPDATE_EVENT,
                  );
                } else {
                  clearNotifs.push(order.orderId.toString());
                }
              }
              // Any order that in auto migration should also be forwarded to OE listeners as there
              // is no corresponding LifeCycle event
              const migrationEvent: IOrderMigrationEvent = {
                order: orderData,
                xferEvent: event,
              };
              logger.out.info(
                `ordp-s - Dispatching oe migration event for order ${orderData.orderRef}`,
              );
              this.modelOrderMigrationSubject.next(migrationEvent);
            } else {
              clearNotifs.push(order.orderId.toString());
            }
            this.fireModelUpdateEvent(
              order,
              Object.assign({}, xferData, { evtMsgId: event.messageId }),
            );
          }
        }
      }
      logger.out.info("XferEventImpl.Processed");
    }

    if (clearNotifs.length > 0) {
      this.notifService.fireNotificationClearEvent(
        this.notifService.dataToClearEvent(clearNotifs),
        BFXNotificationEventType.ORDER_UPDATE_EVENT,
      );
      // this.fireModelNotificationClearEvent(clearNotifs);
    }
  }

  protected processBulkOrderLifecycleEvent(event: CubiqTS.BulkOrderLifeCycleEventImpl): void {
    //process bulk
    let notifType: BFXNotificationType = this.convertEventTypeToNotifType(event.eventTypeId);
    let appNotifType: BFXAppNotificationType = this.convertEventTypeToAppNotifType(
      event.eventTypeId,
    );
    for (let ievent of event.events) {
      this.logOrderEntryDebugInfo(ievent, "BULK-PROCESSING");
      if (notifType === BFXNotificationType.CANCELLED) {
        if (
          ievent.orderData.cancelUserId &&
          ievent.orderData.cancelUserId.toString() === this.cubiqWorker.getUserId()
        ) {
          notifType = undefined;
          appNotifType = undefined;
        }
      }
      let order: CubiqTS.OrderDataImpl = (ievent as CubiqTS.OrderLifeCycleEventImpl)
        .orderData as CubiqTS.OrderDataImpl;
      if (order != null && this.shouldProcessUpdate(order)) {
        const shouldNotifyFg: boolean = this.notifService.shouldNotify(notifType, order.orderType);
        const shouldAppNotifyFg: boolean = this.notifService.shouldAppNotify(
          appNotifType,
          order.orderType,
        );
        if (this.activeUser.canSeeOrder(order)) {
          try {
            let existingOrder = this.base.getOrderData(order.orderId.toString());
            if (existingOrder != null && order.clientSegment == null) {
              order.clientSegment = existingOrder.clientSegment;
              order.clientIsTraderVisible = existingOrder.clientIsTraderVisible;
            }
          } catch (err) {
            logger.out.warn(
              `${this.constructor.name}: Failed to lookup existing order for processing bulk lifecycle event.`,
              err,
            );
          }

          try {
            this.privatizationService.checkPrivatization(order);
          } catch (err) {
            logger.out.warn(
              `${
                this.constructor.name
              }: Failed to check privatization for order ${order?.orderId?.toString()}`,
              err,
            );
          }

          // should do a selective readapt here only a small subset of data can be updated based on a transfer
          this.base.mapItem(order);
          this.fireModelUpdateEvent(order, {
            source: OrderDataModelUpdateSource.BulkOrderLifeCycleEvent,
            evtTypeId: ievent.eventTypeId,
            evtMsgId: ievent.messageId,
          });
          if (
            order &&
            (order.orderState === CubiqTS.OrderState.EXPIRED ||
              order.orderState === CubiqTS.OrderState.CANCELLED)
          ) {
            this.processNotificationFromBulk(
              order,
              shouldNotifyFg,
              shouldAppNotifyFg,
              notifType,
              appNotifType,
            );
          }
        } else {
          let existingOrder = this.base.getOrderData(order.orderId.toString());
          this.orderTempMap.delete(order.orderId.toString());
          if (existingOrder) {
            this.processNotificationFromBulk(
              order,
              shouldNotifyFg,
              shouldAppNotifyFg,
              notifType,
              appNotifType,
            );
            this.fireModelDeleteEvent(order.orderId.toString());

            // If the order is in transfer and cancel is without acceptance it will not be removed from transfer
            this.fireModelDeleteWithReasonEvent(order.orderId.toString(), "");
          }
        }
      }

      if (this.isInTerminalState(order)) {
        this.ignoreAsTerminal(order.orderId);
      }

      if (CubiqTS.RejectSubType.CANCEL_REJECT == ievent.rejectType) {
        this.fireModelErrorEvent(ievent, ievent.externalClientReference);
      } else {
        this.processOrderLifecycleEventForOrderEntry(ievent);
      }
    }
  }

  /** Set of Order Ids that we have removed due to them being in a terminal state */
  private terminalOrderIds: Set<string> = new Set();

  isALoopReactivating(
    order: CubiqTS.OrderDataImpl,
    event: CubiqTS.OrderLifeCycleEventImpl,
  ): boolean {
    let ret: boolean =
      this.isALoop(order) &&
      ((this.isActive(order) && this.isOrderActiveEvent(order, event)) ||
        (this.isAcceptancePending(order) && this.isOrderAcceptancePendingEvent(order, event)));

    if (ret) {
      this.stopIgnoring(order.orderId);
    }

    return ret;
  }

  isOrderActiveEvent(
    order: CubiqTS.OrderDataImpl,
    event: CubiqTS.OrderLifeCycleEventImpl,
  ): boolean {
    return CubiqTS.FxLifeCycleEventType.ACTIVE == event.eventTypeId;
  }

  isOrderAcceptancePendingEvent(
    order: CubiqTS.OrderDataImpl,
    event: CubiqTS.OrderLifeCycleEventImpl,
  ): boolean {
    return CubiqTS.FxLifeCycleEventType.TI_REQUIRED == event.eventTypeId;
  }

  isFilled(order: CubiqTS.OrderDataImpl): boolean {
    return CubiqTS.OrderState.FILLED == order.orderState;
  }

  isActive(order: CubiqTS.OrderDataImpl): boolean {
    return CubiqTS.OrderState.ACTIVE == order.orderState;
  }

  isAcceptancePending(order: CubiqTS.OrderDataImpl): boolean {
    return (
      CubiqTS.OrderState.ACCEPTANCE_NEEDED === order.orderState ||
      CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === order.orderState
    );
  }

  isALoop(order: CubiqTS.OrderDataImpl): boolean {
    return order?.siblingRelationship?.relationshipType === CubiqTS.OrderRelationshipType.LOOP;
  }

  ignoreAsTerminal(orderId: CubiqTS.Long) {
    const orderIdStr = orderId.toString();
    this.terminalOrderIds.add(orderIdStr);
  }

  stopIgnoring(orderId: CubiqTS.Long) {
    const orderIdStr = orderId.toString();
    this.terminalOrderIds.delete(orderIdStr);
  }

  shouldIgnoreAsTerminal(orderId: CubiqTS.Long): boolean {
    const orderIdStr = orderId.toString();
    return this.terminalOrderIds.has(orderIdStr);
  }

  /**
   * Return true if further events for this order should be ignored
   * @param order
   * @returns
   */
  protected isInTerminalState(order: CubiqTS.OrderDataImpl): boolean {
    const state = order?.orderState;
    // Rejected and deleted is not always terminal but should not be an issue here as there should
    // be no transfer events when order is e.g. locked for modification
    return (
      CubiqTS.OrderState.EXPIRED === state ||
      CubiqTS.OrderState.CANCELLED === state ||
      CubiqTS.OrderState.FILLED === state
    );
  }

  /**
   * Check if order is algo as in terminal state
   * @param order the order to be checked
   * @returns true if the order is both an algo and is in terminal state
   */
  protected isTerminalAlgo(order: CubiqTS.OrderDataImpl): boolean {
    return (
      (order?.orderType === CubiqTS.OrderType.ALGO ||
        order?.orderType === CubiqTS.OrderType.BMALGO) &&
      this.isInTerminalState(order)
    );
  }

  public subscribeForCalendars(): Promise<void> {
    return new Promise<void>((res, rej) => {
      let assetCodes = Array.from(this.base.getActivePairs());
      this.currencyPairCalendarService.requestRun(assetCodes).then(
        () => {
          res();
        },
        (reason) => {
          rej(reason);
        },
      );
    });
  }

  protected assesHotForNotif(
    orderId: string,
    temp: Temperature,
    time: number,
    itemData: CubiqTS.OrderData,
  ) {
    itemData = itemData == null ? this.base.getItem(orderId) : itemData;

    if (
      (itemData != null && itemData.orderState === CubiqTS.OrderState.ACTIVE) ||
      itemData.orderState === CubiqTS.OrderState.TRIGGERED
    ) {
      if (temp === Temperature.Hot) {
        let sendNotif: boolean = false;
        let currentTemp = this.orderTempMap.get(orderId);
        if (currentTemp) {
          if (
            (currentTemp.temp === Temperature.Cold || currentTemp.temp === Temperature.Warm) &&
            (!currentTemp.lastHot ||
              currentTemp.lastHot < Date.now() - this.orderTempCoolOffSecs * 1000)
          ) {
            sendNotif = true;
            this.orderTempMap.set(orderId, { temp: temp, lastHot: time });
          }
        } else {
          sendNotif = true;
          this.orderTempMap.set(orderId, { temp: temp, lastHot: time });
        }
        if (sendNotif) {
          // this.fireModelNotificationEvent(itemData as CubiqTS.OrderDataImpl,{notifType:BFXNotificationType.HOT});
          this.notifService.fireNotificationEvent(
            this.notifService.orderDataToNotification(
              itemData as CubiqTS.OrderDataImpl,
              BFXNotificationType.HOT,
            ),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
          );
        }
      } else {
        let currentTemp = this.orderTempMap.get(orderId);
        if (currentTemp) {
          currentTemp.temp = temp;
        } else {
          this.orderTempMap.set(orderId, { temp: temp, lastHot: undefined });
        }
      }
    }
  }

  /**
   * Method invoked right after priming is completed in order
   * to apply all order update events that have been piled up
   * due to ongoing order priming.
   */
  protected flushPendingOrderUpdates(): void {
    if (this.orderUpdates && this.orderUpdates.length > 0) {
      console.debug("Flushing " + this.orderUpdates.length + " pending order updates.");
      this.orderUpdates.forEach((orderUpdate: PendingOrderUpdate) => {
        try {
          orderUpdate.processEventFunction(orderUpdate.event);
        } catch (error) {
          logger.out.error("Failed to process pending order update event", orderUpdate, error);
        }
      });
      this.orderUpdates = [];
    }
  }

  protected fireModelUpdateByOrderId(orderId: string) {
    let orderData: CubiqTS.OrderDataImpl = this.base.getOrderData(orderId);
    if (orderData != null) {
      this.fireModelUpdateEvent(orderData, {
        source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
      });
    }
  }

  protected fireModelDeleteEvent(id: string, dispatchUpdate: boolean = true): void {
    let ord: CubiqTS.OrderDataImpl = this.base.getOrderData(id);

    if (ord) {
      let ccyPair: CubiqTS.CurrencyPair = this.ccyPairLookupService.getCcyPair(
        ord.baseCode,
        ord.nonBaseCode,
      );
      let ndfEnabled: boolean = ccyPair != null ? ccyPair.ndfEnabledFg : true;
      let rateKeys: string[] = OrderLifecycleRateSubscriptionSource.getRateKeys(ord, ndfEnabled);
      let pOrderMap: Set<string> = this.pairOrderMap.get(ccyPair.base + ccyPair.nonBase);
      if (pOrderMap && pOrderMap.has(id)) {
        pOrderMap.delete(id);
      }
      rateKeys.forEach((rateKey) => {
        let orderMap: Set<string> = this.rateOrderMap.get(rateKey);
        if (orderMap && orderMap.has(id)) {
          orderMap.delete(id);
        }
      });

      this.lockJournal.delete(id);
    }

    super.fireModelDeleteEvent(id, dispatchUpdate);
  }

  protected fireModelDeleteWithReasonEvent(
    id: string,
    reason: string,
    data?: any,
    additionalData?: any,
    dispatchUpdate: boolean = true,
  ): void {
    super.fireModelDeleteWithReasonEvent(id, reason, data, additionalData, dispatchUpdate);
  }

  protected fireModelUpdateEvent(
    rawData: any,
    additionalData: any = null,
    dispatchUpdate: boolean = true,
  ): any {
    if (rawData != null) {
      let orderData: CubiqTS.OrderDataImpl = this.base.preprocessOrderData(
        rawData as CubiqTS.OrderDataImpl,
      );

      let ccyPair: CubiqTS.CurrencyPair = this.ccyPairLookupService.getCcyPair(
        orderData.baseCode,
        orderData.nonBaseCode,
      );
      let ndfEnabled: boolean = ccyPair != null ? ccyPair.ndfEnabledFg : true;
      let rateKeys: string[] = OrderLifecycleRateSubscriptionSource.getRateKeys(
        orderData,
        ndfEnabled,
      );
      let pairKey: string = orderData.baseCode + orderData.nonBaseCode;
      let pOrderMap: Set<string> = this.pairOrderMap.get(pairKey);
      if (!pOrderMap) {
        pOrderMap = new Set();
        this.pairOrderMap.set(pairKey, pOrderMap);
      }
      if (!pOrderMap.has(orderData.orderId.toString())) {
        pOrderMap.add(orderData.orderId.toString());
      }

      rateKeys.forEach((rateKey) => {
        let orderMap: Set<string> = this.rateOrderMap.get(rateKey);

        if (orderMap == null) {
          orderMap = new Set();
          this.rateOrderMap.set(rateKey, orderMap);
        }

        if (!orderMap.has(orderData.orderId.toString())) {
          orderMap.add(orderData.orderId.toString());
        }
      });

      this.fireAdaptedDataUpdateEvent(orderData, additionalData, dispatchUpdate);

      return orderData;
    }
    return null;
  }

  protected fireModelErrorEvent(
    rawData: CubiqTS.OrderLifeCycleEvent,
    externalReference: string,
  ): void {
    if (externalReference != null) {
      let data: any = { ref: externalReference };

      if (data != null && rawData != null) {
        Object.assign(data, rawData);
      }
      this.logOrderEntryDebugInfo(
        data.ref,
        "FWD => ERROR Event",
        ExtendedOrderEntryLogType.TEXT_REF,
      );
      this.modelErrorEventSubject.next(data);
      // this.modelUpdateEventPorts.forEach((port: MessagePort) => {
      //     try {
      //         port.postMessage({ modelEventType: LookupEventType.ERROR, payload: data });
      //     } catch (ex) {
      //         logger.out.error(ex);
      //     }
      // });
    } else {
      this.logOrderEntryDebugInfo(
        null,
        `FWD => ERROR === FAIL`,
        ExtendedOrderEntryLogType.TEXT_ONLY,
      );
    }
  }

  protected postprocessPrimedData(): void {
    let allOrders: Array<CubiqTS.OrderDataImpl> = this.base.getAll();

    for (let i: number = 0; i < allOrders.length; i++) {
      let order: CubiqTS.OrderDataImpl = allOrders[i];
      order = this.base.preprocessOrderData(order);
      if (order.lockType == CubiqTS.LockType.CANCEL_LOCK) {
        order.lockType = null;
      } else if (order.lockType == CubiqTS.LockType.FILL_LOCK) {
        try {
          // don't check this detailFillIds for pts
          this.rateCache.setDetailFillPtsLock(
            this.ccyPairLookupService.getCcyPair(order.baseCode, order.nonBaseCode),
            order,
          );
        } catch (e) {
          logger.out.error(
            `OrderDataPrimaryLookupService.postprocessPrimedData() ` +
              `this.rateCache.setDetailFillPtsLock(%O) failed %O`,
            order,
            e,
          );
        }
      }
      this.resolveRelatedOrders(order);
    }

    this.base.reset();
    allOrders = allOrders.filter(this.checkOrderPropagation);
    this.base.prime(allOrders);
    if (this.queueEvents) {
      this.queueEvents = false;
      this.flushPendingOrderUpdates();
    }
  }

  protected checkOrderPropagation(order: CubiqTS.OrderDataImpl): boolean {
    let propagate: boolean = false;

    if (checkMonitoredOrderState(order)) {
      propagate = true;

      if (order.orderState == CubiqTS.OrderState.PENDING) {
        propagate =
          order.activationDts != null &&
          order.parentRelationship == null &&
          (order.siblingRelationship == null ||
            order.siblingRelationship.order == null ||
            order.siblingRelationship.order.parentRelationship == null);

        if (
          order.siblingRelationship != null &&
          order.activationDts != null &&
          order.parentRelationship == null &&
          order.siblingRelationship.order &&
          order.siblingRelationship.order.parentRelationship == null
        ) {
          if (order.siblingRelationship.relationshipType == CubiqTS.OrderRelationshipType.LOOP) {
            let siblingOrder: CubiqTS.OrderDataImpl = order.siblingRelationship.order;
            if (siblingOrder) {
              propagate =
                siblingOrder.orderState != CubiqTS.OrderState.ACTIVE &&
                order.orderId.compare(siblingOrder.orderId) < 0;
            }
          }
        }
      }
    }

    return propagate;
  }

  protected resolveRelatedOrders(order: CubiqTS.OrderDataImpl): void {
    if (order.siblingRelationship != null) {
      if (!order.siblingRelationship.order) {
        let siblingOrder: CubiqTS.OrderDataImpl = this.base.getItem(
          order.siblingRelationship.orderId,
        );
        order.siblingRelationship.order = siblingOrder;
      }
    }

    // Handle case when parent is populated but this child is missing
    if (order != null && order.orderId != null && order.orderId.toString != null) {
      let orderId: string = order.orderId.toString();

      try {
        if (order.parentRelationship != null && order.parentRelationship.orderId != null) {
          let parentOrderId = order.parentRelationship.orderId;
          let parent: OrderDataImpl = this.base.getItem(parentOrderId);

          if (parent != null) {
            let children = parent.childRelationships;

            if (children == null) {
              parent.childRelationships = children = [];
            }

            let relationshipExists: boolean = children.some((r) => r.orderId === orderId);
            if (!relationshipExists) {
              let relationship: CubiqTS.Relationship = new CubiqTS.Relationship();
              relationship.relationshipType = order.parentRelationship.relationshipType;
              relationship.orderId = orderId;
              children.push(relationship);
            }

            // Try resolving missing order data
            try {
              let childMissingOrderData: CubiqTS.Relationship[] = children.filter(
                (r) => r.orderId === orderId && r.order == null,
              );
              childMissingOrderData.forEach((c) => (c.order = order));
            } catch (e) {
              logger.out.warn(
                `${this.constructor.name} Order ${orderId.toString()} has a ` +
                  `parent ${parentOrderId.toString()} but could not resolve order data.`,
              );
            }
          } else {
            logger.out.warn(
              `${this.constructor.name} Order ${orderId.toString()} has a ` +
                `parent ${parentOrderId.toString()} but parent seems to be missing during prime.`,
            );
          }
        }
      } catch (ex) {
        logger.out.error(
          `${this.constructor.name} Failed to determine parent relationship` +
            ` of order ${orderId}, ${ex}`,
        );
      }
    }
  }

  public getOrderData(id: string): Promise<CubiqTS.OrderDataImpl> {
    return new Promise<CubiqTS.OrderDataImpl>((res) => {
      res(this.base.getOrderData(id));
    });
  }

  /* Used to get clOrderId when initiating order entry for modify */
  public getOrderReference(id: string): Promise<string> {
    return new Promise<string>((res) => {
      let orderData = this.base.getOrderData(id);
      orderData != null && orderData instanceof CubiqTS.OrderDataImpl
        ? res(orderData.clOrdID)
        : res(null);
    });
  }

  public getModelOrderMigrationSubject(): Subject<IOrderMigrationEvent> {
    return this.modelOrderMigrationSubject;
  }

  public getModifyPreInitDataForOrderId(id: string): Promise<any> {
    return new Promise<any>((res, rej) => {
      let orderData = this.base.getOrderData(id);
      if (orderData != null && orderData instanceof CubiqTS.OrderDataImpl) {
        let isPrivate: boolean = StaticDataPrivatizationType.hasPrivatization(orderData);
        res({ ref: orderData.clOrdID, privatized: isPrivate });
        return;
      }

      // open view order details ticket
      this.detailedOrderStatusService.requestMediatedOrderDetails(id).then(
        (orderData) => {
          if (orderData != null && orderData.orderDetails) {
            let theOrder = orderData.orderDetails.get(id);
            let isPrivate: boolean = StaticDataPrivatizationType.hasPrivatization(theOrder);
            res({ ref: theOrder.clOrderId, privatized: isPrivate });
            return;
          }
          rej(null);
        },
        () => {
          rej(null);
        },
      );
    });
  }

  public getOrderDataForIds(ids: Array<string>): Promise<Array<CubiqTS.OrderDataImpl>> {
    return new Promise<Array<CubiqTS.OrderDataImpl>>((res) => {
      res(this.base.getOrderDataForIds(ids));
    });
  }

  public hasOrdersForRateKey(subscriptionKey: string): Promise<boolean> {
    return new Promise<boolean>((res) => {
      if (this.rateOrderMap) {
        let orderMap: Set<string> = this.rateOrderMap.get(subscriptionKey);
        if (orderMap && orderMap.size > 0) {
          res(true);
        }
      }
      res(false);
    });
  }

  /**
   * Not in use, do not delete
   * This is a incomplete implementation of function that should provide evaluation
   * of non-dealable rates per order which is not needed now (but will in the future),
   * since we dont have forwards at the moment.
   */
  public getNonDealableForOrderIds(ids: Array<string>): Promise<Map<string, boolean>> {
    return new Promise<Map<string, boolean>>(() => {
      let orderData: Array<CubiqTS.OrderDataImpl> = this.base.getOrderDataForIds(ids);
      orderData.forEach(() => {
        // let rateKey: string = OrderLifecycleRateSubscriptionSource.getRateKey(orderItem);
        //let orderMap: any = this.rateOrderMap[rateKey];
      });
    });
  }

  public addOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res) => {
      if (!this.orderEntryReferences.has(ref)) {
        this.orderEntryReferences.add(ref);
        this.logOrderEntryDebugInfo(ref, "Order-Update", ExtendedOrderEntryLogType.LISTENER_ADD);
        res(true);
      } else {
        res(false);
      }
    });
  }

  public removeOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res) => {
      if (this.orderEntryReferences.has(ref)) {
        this.orderEntryReferences.delete(ref);
        this.logOrderEntryDebugInfo(ref, "Order-Update", ExtendedOrderEntryLogType.LISTENER_REMOVE);
        res(true);
      } else {
        res(false);
      }
    });
  }

  public hasOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res) => {
      res(this.orderEntryReferences.has(ref));
    });
  }

  public getAllOrderDatas(): Promise<Array<CubiqTS.OrderDataImpl>> {
    return new Promise<Array<CubiqTS.OrderDataImpl>>((res, rej) => {
      let allOrderData = this.base.getAll();
      res(allOrderData);
    });
  }

  public onOrderFlash(orderId: string): void {
    let orderData: CubiqTS.OrderDataImpl = this.base.getOrderData(orderId);
    if (orderData != null) {
      this.fireModelUpdateEvent(orderData, {
        source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
        uiFlashEvent: true,
      });
    }
  }

  /**
   * Check if an algo forward is transitioning from cancel/expiry fill pending states
   * to respective terminal state. Needed to determine if fill partial lifecycle event needs to be
   * processed
   * @param newOrder order data from new lifecycle event
   * @param oldOrder order data from existing (in memory) lifecycle event
   */
  private isAlgoForwardOrderTerminating(
    newOrder: CubiqTS.OrderDataImpl,
    oldOrder: CubiqTS.OrderDataImpl,
  ): boolean {
    let result: boolean = false;

    if (newOrder != null && oldOrder != null) {
      let cancelRoll: boolean =
        oldOrder.orderState === CubiqTS.OrderState.CANCEL_PENDING_FWDS &&
        newOrder.orderState === CubiqTS.OrderState.CANCELLED;

      let expiryRoll: boolean =
        oldOrder.orderState === CubiqTS.OrderState.EXPIRY_PENDING_FWDS &&
        newOrder.orderState === CubiqTS.OrderState.EXPIRED;

      let isAlgo: boolean = newOrder.orderType === CubiqTS.OrderType.ALGO;

      result = isAlgo && newOrder.product != CubiqTS.Product.SPT && (cancelRoll || expiryRoll);
    }

    return result;
  }

  /**
   * Checks orders in memory that need to have their inflight state changed and publish a notification
   * to refresh the order data in the blotter.
   * @param message the inflight check status change containing the new infligh check state and the order IDs affected
   */
  protected processInflightApplicationMessage(message: CubiqTS.ApplicationMessageEventImpl): void {
    try {
      if (
        message.messageType === EVENT_TYPE_INFLIGHT_CHECK_STATE &&
        message.message instanceof CubiqTS.InflightCheckStateEventImpl
      ) {
        logger.out.info(
          `${this.constructor.name}: Processing inflight change state ${message.message}`,
        );
        let event: CubiqTS.InflightCheckStateEventImpl = message.message;
        if (event.orderIds != null && event.orderIds.length > 0) {
          let orders: OrderDataImpl[] = event.orderIds
            .map((o) => this.base.getItem(o.toString()))
            .filter((o) => o != null);
          orders = orders.filter((o) => this.activeUser.canSeeOrder(o));

          orders.forEach((o) => {
            o.inflightInclusionState = event.inflightInclusionState;
            this.onOrderFlash(o.orderId.toString());
          });

          logger.out.info(
            `${this.constructor.name}: ${orders.length} algo order processed for inflight state change our of ${event.orderIds.length} total.`,
          );
        }
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Failed to process application message event: ${e}`,
      );
    }
  }

  private shouldClear(event: CubiqTS.OrderLifeCycleEvent): boolean {
    let shouldClear: boolean = true;

    try {
      if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.FILLED) {
        let existingOrder = this.base.getOrderData(event.orderData.orderId.toString());

        if (existingOrder) {
          if (
            existingOrder.orderState === CubiqTS.OrderState.FILLED &&
            event.orderData.orderState === CubiqTS.OrderState.FILLED
          ) {
            shouldClear = false;
          }
        }
      }
    } catch (e) {
      logger.out.warn(`${this.constructor.name}: Failed to check if event should be cleared: ${e}`);
    }

    return shouldClear;
  }

  /**
   * Returns the transfer update subject used for listening for transfer
   * events that do not contain order data. This are events are generated
   * when the transfer flow is interrupted by another action and should
   * be observer only to update respective views that the action will fail
   * for certain orders
   */
  public getTransferUpdateSubject(): Subject<any> {
    return this.transferUpdateSubject;
  }

  /**
   * Handle notifications based on order data from bulk
   * order lifecycle event
   *
   * @param order order data from a bulk order lifecycle event
   * @param shouldNotify should create a popup notification
   * @param shouldAppNotify should create application notification
   * @param notifType popup notification type
   * @param appNotifType app notification type
   */
  protected processNotificationFromBulk(
    order: OrderDataImpl,
    shouldNotify: boolean,
    shouldAppNotify: boolean,
    notifType: BFXNotificationType,
    appNotifType: BFXAppNotificationType,
  ): void {
    try {
      let existingOrder = this.base.getOrderData(order.orderId.toString());
      if (existingOrder) {
        if (shouldNotify && notifType !== undefined) {
          // this.fireModelNotificationEvent(existingOrder,{notifType:notifType});
          this.notifService.fireNotificationEvent(
            this.notifService.orderDataToNotification(existingOrder, notifType),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
          );
          // this.notifService.orderDataToNotification(existingOrder, notifType)
        } else {
          this.notifService.fireNotificationClearEvent(
            this.notifService.dataToClearEvent([order.orderId.toString()]),
            BFXNotificationEventType.ORDER_UPDATE_EVENT,
          );
          // this.fireModelNotificationClearEvent([order.orderId.toString()]);
        }
        if (shouldAppNotify && appNotifType !== undefined) {
          // this.fireAppModelNotificationEvent(existingOrder,{appNotifType:appNotifType});
          this.notifService.fireAppNotificationEvent(
            this.notifService.orderDataToNotification(existingOrder, notifType),
            BFXAppNotificationEventType.ORDER_UPDATE_EVENT,
          );
        }
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Error while processing events for
         notification from BulkOrderLifeCycleEvent: ${e}`,
        e,
      );
    }
  }

  /**
   * Handle adjusted tenor holiday event by processing all orders in the base service
   * that match the pair and tenor in the event and set the adjusted tenor
   *
   * @param event the AdjustedTenorForHolidayEvent
   */
  protected processAdjustedTenorForHoliday(event: CubiqTS.AdjustedTenorForHolidayEventImpl): void {
    try {
      let pairs: string[] = [
        event.baseCcyCd + event.nonBaseCcyCd,
        event.nonBaseCcyCd + event.baseCcyCd,
      ];

      this.base
        .getAll()
        .filter((o) => pairs.includes(o.baseCode + o.nonBaseCode) && o.settleTenor == event.tenor)
        .map((o) => {
          o.adjustedSettleTenor = event.adjustedTenor;
          return o.orderId.toString();
        })
        .forEach((orderId) => this.fireModelUpdateByOrderId(orderId));
    } catch (err) {
      logger.out.error(
        `${this.constructor.name}: Unexpected error` +
          ` processing ${CubiqTS.AdjustedTenorForHolidayEventImpl.name} event.`,
        err,
      );
    }
  }

  @ModelUpdate(CubiqTS.ClientImpl)
  protected onClientUpdate(client: CubiqTS.ClientImpl, order: CubiqTS.OrderDataImpl): boolean {
    return order.clientCode == client.clientId;
  }

  @ModelUpdate(CubiqTS.AccountImpl)
  protected onAccountUpdate(account: CubiqTS.AccountImpl, order: CubiqTS.OrderDataImpl): boolean {
    return order.accountCode == account.id;
  }

  @ModelUpdate(CubiqTS.CurrencyImpl)
  protected onCurrencyUpdate(
    currency: CubiqTS.CurrencyImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return order.baseCode == currency.id || order.nonBaseCode == currency.id;
  }

  @ModelUpdate(CubiqTS.CurrencyPairImpl)
  protected onCurrencyPairUpdate(
    currencyPair: CubiqTS.CurrencyPairImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return order.baseCode == currencyPair.base && order.nonBaseCode == currencyPair.nonBase;
  }

  @ModelUpdate(CubiqTS.TradingGroupImpl)
  protected onTraderGroupUpdate(
    traderGroup: CubiqTS.TradingGroupImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return (
      this.compareIds(order.deskId, traderGroup.groupId) ||
      this.compareIds(order.targetDeskId, traderGroup.groupId)
    );
  }

  @ModelUpdate(CubiqTS.SalesGroupImpl)
  protected onSalesGroupUpdate(
    salesGroup: CubiqTS.SalesGroupImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return this.compareIds(order.salesGroupId, salesGroup.groupId);
  }

  @ModelUpdate(CubiqTS.UserImpl)
  protected onUserUpdate(user: CubiqTS.UserImpl, order: CubiqTS.OrderDataImpl): boolean {
    return (
      this.compareIds(order.entryUserId, user.userId) ||
      this.compareIds(order.lockUserId, user.userId) ||
      this.compareIds(order.transferUserId, user.userId)
    );
  }

  @ModelUpdate(CubiqTS.CurrencyPairCalendarImpl)
  protected onCurrencyPairCalendarUpdate(
    calendar: CubiqTS.CurrencyPairCalendarImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    logger.out.info(`${calendar} ${order}`);
    return false;
  }
}
